import { ARCHIVE_SEARCH_MODES, ARCHIVE_SEARCH_SORTS } from "../services/search/archiveSearchProtocol.js";
import { normalizeArchiveSearchIndex } from "../services/search/archiveSearchIndex.js";
import { sortSearchResults } from "../services/search/lexicalSearch.js";
import { getShowsLikeAnchorTitle } from "../services/search/semanticSearch.js";
import { normalizeSearchText } from "../services/search/searchText.js";
import { cleanViewerFacingCopyList } from "../utils/viewerFacingCopy.js";

const RRF_K = 60;
const LEXICAL_RRF_WEIGHT = 1.2;
const SEMANTIC_MATCH_LEXICAL_RRF_WEIGHT = 0.25;
const SEMANTIC_MATCH_RRF_WEIGHT = 1.4;
const RETAINED_LEXICAL_ONLY_RRF_WEIGHT = 1.7;
const DEFAULT_RELEVANCE_LIMIT = 24;
const WATCHLIST_BOOST = 0.05;
const MMR_LAMBDA = 0.7;
const SEMANTIC_FUSION_LEXICAL_ONLY_WEIGHT = 0.45;
const NEGATED_MATCH_SCORE_WEIGHT = 0.05;
const METADATA_MISMATCH_SCORE_WEIGHT = 0.25;
const METADATA_MATCH_SCORE_WEIGHT = 1.25;
const DISPLAY_LIMITS = Object.freeze({
  exactTitle: 6,
  showsLike: 8,
  semantic: 10,
  semanticStrong: 12,
  metadata: 20,
  metadataStrong: 24,
  weak: 6,
  default: 8,
});

function toNormalizedTokenSet(values = []) {
  return new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => normalizeSearchText(value))
      .filter(Boolean)
  );
}

function jaccardSimilarity(leftSet = new Set(), rightSet = new Set()) {
  if (!leftSet.size || !rightSet.size) {
    return 0;
  }

  let intersection = 0;
  for (const value of leftSet) {
    if (rightSet.has(value)) {
      intersection += 1;
    }
  }

  const union = new Set([...leftSet, ...rightSet]).size;
  return union ? intersection / union : 0;
}

function buildShowSignalSet(show = {}) {
  return toNormalizedTokenSet([
    ...(Array.isArray(show.tasteTags) ? show.tasteTags : []),
    ...(Array.isArray(show.genres) ? show.genres : []),
  ]);
}

function getExactTitleMatch(show = {}, normalizedQuery = "") {
  return Boolean(normalizedQuery && normalizeSearchText(show.title) === normalizedQuery);
}

function fieldContainsPhrase(field = "", query = "") {
  if (!field || !query) return false;
  return ` ${field} `.includes(` ${query} `);
}

function tokenizeNormalizedText(value = "") {
  return normalizeSearchText(value).split(" ").filter(Boolean);
}

function getShowSearchText(show = {}) {
  return normalizeSearchText([
    show.title,
    show.source,
    show.monthLabel,
    show.yearMonth,
    show.releaseDate,
    show.summary,
    ...(Array.isArray(show.genres) ? show.genres : []),
    ...(Array.isArray(show.tasteTags) ? show.tasteTags : []),
  ].filter(Boolean).join(" "));
}

function queryReferencesShowMetadata(show = {}, normalizedQuery = "") {
  if (!normalizedQuery) {
    return false;
  }
  const source = normalizeSearchText(show.source);
  const month = normalizeSearchText([show.monthLabel, show.yearMonth].filter(Boolean).join(" "));
  const title = normalizeSearchText(show.title);
  return Boolean(
    fieldContainsPhrase(title, normalizedQuery) ||
    fieldContainsPhrase(source, normalizedQuery) ||
    fieldContainsPhrase(month, normalizedQuery) ||
    (source && fieldContainsPhrase(normalizedQuery, source)) ||
    (month && fieldContainsPhrase(normalizedQuery, month))
  );
}

function getQueryMetadataSignals(candidates = [], normalizedQuery = "") {
  if (!normalizedQuery) {
    return { hasMetadataIntent: false };
  }
  const hasYearOrMonth = /\b(?:january|february|march|april|may|june|july|august|september|october|november|december|20\d{2})\b/.test(normalizedQuery);
  const hasSource = candidates.some((show) => {
    const source = normalizeSearchText(show?.source);
    return source && fieldContainsPhrase(normalizedQuery, source);
  });
  return {
    hasMetadataIntent: hasYearOrMonth || hasSource,
  };
}

function getNegatedTerms(normalizedQuery = "") {
  if (!normalizedQuery) {
    return [];
  }
  const tokens = tokenizeNormalizedText(normalizedQuery);
  const negated = [];
  const markers = new Set(["no", "not", "without", "exclude", "excluding"]);
  for (let index = 0; index < tokens.length; index += 1) {
    if (!markers.has(tokens[index])) {
      continue;
    }
    for (let offset = 1; offset <= 3 && index + offset < tokens.length; offset += 1) {
      const token = tokens[index + offset];
      if (["and", "or", "with", "but"].includes(token)) {
        continue;
      }
      const nextToken = tokens[index + offset + 1];
      if (token === "love" && /^triangles?$/.test(nextToken || "")) {
        negated.push("love triangle", "triangle");
        offset += 1;
        continue;
      }
      negated.push(token.replace(/s$/, ""));
    }
  }
  return [...new Set(negated.filter((term) => term.length > 2))];
}

function showMatchesNegatedTerms(show = {}, negatedTerms = []) {
  if (negatedTerms.length === 0) {
    return false;
  }
  const text = getShowSearchText(show);
  return negatedTerms.some((term) => {
    const plural = `${term}s`;
    return fieldContainsPhrase(text, term) || fieldContainsPhrase(text, plural);
  });
}

function isShowsLikeAnchor(show = {}, anchorTitle = "") {
  if (!anchorTitle) {
    return false;
  }
  return normalizeSearchText(show.title) === anchorTitle;
}

function shouldDemoteLexicalOnlyResult({ lexical = null, semantic = null, base = {}, normalizedQuery = "" } = {}) {
  if (!lexical || semantic) {
    return false;
  }
  if (getExactTitleMatch(base, normalizedQuery) || queryReferencesShowMetadata(base, normalizedQuery)) {
    return false;
  }
  return true;
}

function rankForList(results = []) {
  const rankBySlug = new Map();
  results.forEach((result, index) => {
    if (result?.slug) {
      rankBySlug.set(result.slug, index + 1);
    }
  });
  return rankBySlug;
}

function computeMergedScore({ lexicalRank, semanticRank, demoteLexicalOnly = false } = {}) {
  if (semanticRank) {
    let score = SEMANTIC_MATCH_RRF_WEIGHT / (RRF_K + semanticRank);
    if (lexicalRank) {
      score += SEMANTIC_MATCH_LEXICAL_RRF_WEIGHT / (RRF_K + lexicalRank);
    }
    return score;
  }

  const lexicalOnlyWeight = demoteLexicalOnly
    ? LEXICAL_RRF_WEIGHT * SEMANTIC_FUSION_LEXICAL_ONLY_WEIGHT
    : RETAINED_LEXICAL_ONLY_RRF_WEIGHT;
  return lexicalRank ? lexicalOnlyWeight / (RRF_K + lexicalRank) : 0;
}

function buildMergedResults({ lexicalResults = [], semanticResults = [], normalizedQuery = "" } = {}) {
  const lexicalBySlug = new Map();
  const semanticBySlug = new Map();

  lexicalResults.forEach((result) => {
    if (result?.slug) {
      lexicalBySlug.set(result.slug, result);
    }
  });

  semanticResults.forEach((result) => {
    if (result?.slug) {
      semanticBySlug.set(result.slug, result);
    }
  });

  const lexicalRanks = rankForList(lexicalResults);
  const semanticRanks = rankForList(semanticResults);
  const candidateShows = [...lexicalBySlug.values(), ...semanticBySlug.values()];
  const metadataSignals = getQueryMetadataSignals(candidateShows, normalizedQuery);
  const negatedTerms = getNegatedTerms(normalizedQuery);
  const showsLikeAnchorTitle = getShowsLikeAnchorTitle(normalizedQuery);
  const merged = [];
  const seen = new Set();
  const slugs = [...lexicalBySlug.keys(), ...semanticBySlug.keys()];

  for (const slug of slugs) {
    if (seen.has(slug)) {
      continue;
    }
    seen.add(slug);

    const lexical = lexicalBySlug.get(slug) || null;
    const semantic = semanticBySlug.get(slug) || null;
    const base = semantic || lexical || {};
    if (isShowsLikeAnchor(base, showsLikeAnchorTitle)) {
      continue;
    }
    const demoteLexicalOnly = shouldDemoteLexicalOnlyResult({
      lexical,
      semantic,
      base,
      normalizedQuery,
    });
    const metadataMatch = queryReferencesShowMetadata(base, normalizedQuery);
    const negatedMatch = showMatchesNegatedTerms(base, negatedTerms);
    const metadataWeight = metadataSignals.hasMetadataIntent
      ? metadataMatch ? METADATA_MATCH_SCORE_WEIGHT : METADATA_MISMATCH_SCORE_WEIGHT
      : 1;
    const negationWeight = negatedMatch ? NEGATED_MATCH_SCORE_WEIGHT : 1;
    const mergedResult = {
      ...lexical,
      ...semantic,
      ...base,
      score: computeMergedScore({
        lexicalRank: lexicalRanks.get(slug),
        semanticRank: semanticRanks.get(slug),
        demoteLexicalOnly,
      }) * metadataWeight * negationWeight,
      sourceRank: {
        lexical: lexicalRanks.get(slug) || null,
        semantic: semanticRanks.get(slug) || null,
      },
      exactTitleMatch: getExactTitleMatch(base, normalizedQuery),
      lexicalOnlyDemoted: demoteLexicalOnly,
      metadataIntentMatched: metadataSignals.hasMetadataIntent ? metadataMatch : null,
      negatedMatch,
      semanticMatchReason: semantic?.matchReason || null,
      lexicalMatchReason: lexical?.matchReason || null,
    };

    if (mergedResult.exactTitleMatch) {
      mergedResult.score = Math.max(mergedResult.score, 2);
    }
    if (semantic?.matchReason) {
      mergedResult.matchReason = semantic.matchReason;
    } else if (lexical?.matchReason) {
      mergedResult.matchReason = lexical.matchReason;
    }

    merged.push(mergedResult);
  }

  return merged;
}

function rerankWithMmr(results = [], { limit = DEFAULT_RELEVANCE_LIMIT, lambda = MMR_LAMBDA } = {}) {
  if (results.length <= 1) {
    return results.slice(0, limit);
  }

  const selected = [];
  const available = [...results];
  const topScore = Math.max(...available.map((result) => Number(result.score) || 0), 0) || 1;

  while (selected.length < limit && available.length > 0) {
    let bestIndex = 0;
    let bestScore = -Infinity;

    available.forEach((candidate, index) => {
      const relevance = Math.max(0, (Number(candidate.score) || 0) / topScore);
      const candidateSignals = buildShowSignalSet(candidate);
      const redundancy = selected.reduce((max, selectedItem) => {
        const selectedSignals = buildShowSignalSet(selectedItem);
        return Math.max(max, jaccardSimilarity(candidateSignals, selectedSignals));
      }, 0);
      const mmrScore = (lambda * relevance) - ((1 - lambda) * redundancy);
      if (mmrScore > bestScore) {
        bestScore = mmrScore;
        bestIndex = index;
      }
    });

    const [chosen] = available.splice(bestIndex, 1);
    selected.push({
      ...chosen,
      score: Number(Math.max(bestScore, 0).toFixed(6)),
      mmrScore: Number(bestScore.toFixed(6)),
    });
  }

  return selected;
}

function splitExactTitleMatches(results = []) {
  const exactMatches = [];
  const remainder = [];

  results.forEach((result) => {
    if (result?.exactTitleMatch) {
      exactMatches.push(result);
      return;
    }
    remainder.push(result);
  });

  return { exactMatches, remainder };
}

function applyMetadataIntentFilter(results = []) {
  const hasMetadataIntent = results.some((result) => result?.metadataIntentMatched !== null && result?.metadataIntentMatched !== undefined);
  if (!hasMetadataIntent) {
    return results;
  }
  const metadataMatches = results.filter((result) => result?.metadataIntentMatched || result?.exactTitleMatch);
  return metadataMatches.length >= 3 ? metadataMatches : results;
}

function hasMetadataStyleQuery(query = "") {
  if (!query) {
    return false;
  }
  return /\b(?:19\d{2}|20\d{2}|january|february|march|april|may|june|july|august|september|october|november|december)\b/.test(query);
}

export function getArchiveSearchDisplayInfo({
  query = "",
  results = [],
  searchResult = null,
} = {}) {
  const normalizedQuery = normalizeSearchText(query);
  const resultList = Array.isArray(results) ? results : [];
  const resultCount = resultList.length;
  if (resultCount === 0) {
    return {
      intent: "empty",
      limit: 0,
    };
  }

  const topScore = Number(searchResult?.topScore ?? resultList[0]?.score ?? 0);
  const exactTitleMatches = resultList.filter((result) => result?.exactTitleMatch).length;
  const hasShowsLikeIntent = Boolean(getShowsLikeAnchorTitle(normalizedQuery));
  const hasMetadataIntent = resultList.some((result) => queryReferencesShowMetadata(result, normalizedQuery)) || hasMetadataStyleQuery(normalizedQuery);
  const isSemanticResult = Boolean(searchResult?.mode && searchResult.mode !== ARCHIVE_SEARCH_MODES.LEXICAL);
  const isWeakResult = topScore > 0 ? topScore < 0.45 : resultCount <= DISPLAY_LIMITS.weak;

  if (exactTitleMatches > 0 && normalizedQuery.split(" ").filter(Boolean).length <= 4) {
    return {
      intent: "exact-title",
      limit: Math.min(DISPLAY_LIMITS.exactTitle, resultCount),
    };
  }

  if (hasShowsLikeIntent) {
    return {
      intent: "shows-like",
      limit: Math.min(topScore >= 0.85 ? DISPLAY_LIMITS.semanticStrong : DISPLAY_LIMITS.showsLike, resultCount),
    };
  }

  if (hasMetadataIntent) {
    return {
      intent: "metadata",
      limit: Math.min(topScore >= 0.8 ? DISPLAY_LIMITS.metadataStrong : DISPLAY_LIMITS.metadata, resultCount),
    };
  }

  if (isWeakResult) {
    return {
      intent: "weak",
      limit: Math.min(DISPLAY_LIMITS.weak, resultCount),
    };
  }

  if (isSemanticResult) {
    return {
      intent: "semantic",
      limit: Math.min(topScore >= 0.85 ? DISPLAY_LIMITS.semanticStrong : DISPLAY_LIMITS.semantic, resultCount),
    };
  }

  return {
    intent: "default",
    limit: Math.min(DISPLAY_LIMITS.default, resultCount),
  };
}

export function buildArchiveSearchDisplayResults({
  lexicalResult = null,
  semanticResult = null,
  query = "",
  sort = ARCHIVE_SEARCH_SORTS.RELEVANCE,
} = {}) {
  const normalizedQuery = normalizeSearchText(query);
  const cleanResult = (result) => ({
    ...result,
    tasteTags: cleanViewerFacingCopyList(result?.tasteTags, {
      protectedPhrases: result?.title ? [result.title] : [],
    }),
    matchTags: cleanViewerFacingCopyList(result?.matchTags, {
      protectedPhrases: result?.title ? [result.title] : [],
    }),
  });
  const lexicalResults = Array.isArray(lexicalResult?.results) ? lexicalResult.results.map(cleanResult) : [];
  const semanticResults = Array.isArray(semanticResult?.results) ? semanticResult.results.map(cleanResult) : [];
  const semanticRan = Boolean(semanticResult);

  const merged = semanticRan
    ? buildMergedResults({ lexicalResults, semanticResults, normalizedQuery })
    : lexicalResults.map((result) => ({
      ...result,
      exactTitleMatch: getExactTitleMatch(result, normalizedQuery),
    }));

  const exactPinResults = merged.map((result) => (
    result.exactTitleMatch
      ? { ...result, score: Math.max(Number(result.score) || 0, 2) }
      : result
  ));
  const rankedPool = semanticRan ? applyMetadataIntentFilter(exactPinResults) : exactPinResults;

  if (sort !== ARCHIVE_SEARCH_SORTS.RELEVANCE) {
    const sorted = sortSearchResults(rankedPool, sort);
    const displayInfo = getArchiveSearchDisplayInfo({
      query,
      results: sorted,
      searchResult: {
        mode: semanticRan ? semanticResult.mode || ARCHIVE_SEARCH_MODES.SEMANTIC : ARCHIVE_SEARCH_MODES.LEXICAL,
        topScore: sorted[0]?.score || 0,
      },
    });
    return {
      mode: semanticRan ? semanticResult.mode || ARCHIVE_SEARCH_MODES.SEMANTIC : ARCHIVE_SEARCH_MODES.LEXICAL,
      corpusVersion: semanticResult?.corpusVersion || lexicalResult?.corpusVersion || null,
      threshold: semanticResult?.threshold ?? null,
      topScore: sorted[0]?.score || 0,
      displayIntent: displayInfo.intent,
      displayLimit: displayInfo.limit,
      unknownSlugs: semanticResult?.unknownSlugs || [],
      results: sorted,
    };
  }

  const { exactMatches, remainder } = splitExactTitleMatches(rankedPool);
  const fusedResults = semanticRan
    ? sortSearchResults(remainder, ARCHIVE_SEARCH_SORTS.RELEVANCE)
    : sortSearchResults(rankedPool, ARCHIVE_SEARCH_SORTS.RELEVANCE);
  const reranked = semanticRan
    ? rerankWithMmr(fusedResults, {
      limit: fusedResults.length,
      lambda: MMR_LAMBDA,
    })
    : fusedResults;
  const results = semanticRan
    ? sortSearchResults([...exactMatches, ...reranked], ARCHIVE_SEARCH_SORTS.RELEVANCE)
    : sortSearchResults(rankedPool, ARCHIVE_SEARCH_SORTS.RELEVANCE);
  const displayInfo = getArchiveSearchDisplayInfo({
    query,
    results,
    searchResult: {
      mode: semanticRan ? semanticResult.mode || ARCHIVE_SEARCH_MODES.SEMANTIC : ARCHIVE_SEARCH_MODES.LEXICAL,
      topScore: results[0]?.score || 0,
    },
  });

  return {
    mode: semanticRan ? semanticResult.mode || ARCHIVE_SEARCH_MODES.SEMANTIC : ARCHIVE_SEARCH_MODES.LEXICAL,
    corpusVersion: semanticResult?.corpusVersion || lexicalResult?.corpusVersion || null,
    threshold: semanticResult?.threshold ?? null,
    topScore: results[0]?.score || 0,
    displayIntent: displayInfo.intent,
    displayLimit: displayInfo.limit,
    unknownSlugs: semanticResult?.unknownSlugs || [],
    results,
  };
}

function entrySignalSet(entry = {}) {
  return toNormalizedTokenSet([
    ...(Array.isArray(entry.tasteTags) ? entry.tasteTags : []),
    ...(Array.isArray(entry.genres) ? entry.genres : []),
  ]);
}

function resultSignalSet(result = {}) {
  return buildShowSignalSet(result);
}

export function applyWatchlistBoost(results = [], watchlistEntries = [], { sort = ARCHIVE_SEARCH_SORTS.RELEVANCE } = {}) {
  const entries = Array.isArray(watchlistEntries) ? watchlistEntries.filter(Boolean) : [];
  if (entries.length === 0) {
    return results;
  }

  const boosted = results.map((result) => {
    const resultSignals = resultSignalSet(result);
    const hasTasteOverlap = entries.some((entry) => {
      const entrySignals = entrySignalSet(entry);
      const tasteOverlap = Array.from(resultSignals).filter((signal) => entrySignals.has(signal)).length;
      if (tasteOverlap >= 2) {
        return true;
      }
      const resultGenres = toNormalizedTokenSet(result.genres);
      const entryGenres = toNormalizedTokenSet(entry.genres);
      for (const genre of resultGenres) {
        if (entryGenres.has(genre)) {
          return true;
        }
      }
      return false;
    });

    if (!hasTasteOverlap) {
      return result;
    }

    return {
      ...result,
      score: Number(((Number(result.score) || 0) + WATCHLIST_BOOST).toFixed(6)),
      watchlistBoosted: true,
      watchlistBoostLabel: "Matches your taste",
    };
  });

  if (sort === ARCHIVE_SEARCH_SORTS.RELEVANCE) {
    return sortSearchResults(boosted, sort);
  }

  return boosted;
}

export function pickArchiveSurpriseShow({
  index,
  results = [],
  query = "",
  selectedTags = [],
} = {}) {
  const normalizedIndex = index?.showsBySlug ? index : normalizeArchiveSearchIndex(index);
  const hasQuery = Boolean(normalizeSearchText(query));
  const selectedSet = new Set((Array.isArray(selectedTags) ? selectedTags : []).filter(Boolean));

  let pool = Array.isArray(results) && results.length > 0 ? results : normalizedIndex.shows || [];
  if (pool.length === 0) {
    return null;
  }

  if (!Array.isArray(results) || results.length === 0) {
    if (selectedSet.size > 0) {
      const selectedLabels = new Set(
        [...selectedSet]
          .map((slug) => normalizedIndex.tagsBySlug?.get(slug)?.tag || slug)
          .map((label) => normalizeSearchText(label))
          .filter(Boolean)
      );
      pool = pool.filter((show) => {
        const tasteTags = Array.isArray(show.tasteTags) ? show.tasteTags : [];
        return tasteTags.some((tag) => selectedLabels.has(normalizeSearchText(tag)));
      });
    } else if (hasQuery) {
      const queryTokens = normalizeSearchText(query).split(" ").filter(Boolean);
      pool = pool.filter((show) => {
        const text = normalizeSearchText([
          show.title,
          show.source,
          show.monthLabel,
          ...(Array.isArray(show.tasteTags) ? show.tasteTags : []),
          ...(Array.isArray(show.genres) ? show.genres : []),
        ].filter(Boolean).join(" "));
        return queryTokens.every((token) => text.includes(token));
      });
    }
  }

  if (pool.length === 0) {
    pool = normalizedIndex.shows || [];
  }

  return pool[Math.floor(Math.random() * pool.length)] || null;
}
